/**
 * Contract Template Diagnostics Widget — Zorzutti v2 audit panel.
 *
 * Mounted in the EditCustomerModal next to the GDPR admin panel.
 * Calls the read-only diagnostic endpoint:
 *
 *     GET /api/customers/{customer_id}/contract-template-coverage
 *
 * and renders an at-a-glance coverage % with the list of missing
 * mandatory fields so the Legal team can audit, per customer,
 * whether the platform is collecting every datum required by the
 * Zorzutti template — before the Fase 2 swap of the final legal text.
 */
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { FileText, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_BADGE = {
  customer: "bg-lojack-blue-500/15 text-lojack-blue-300 border-lojack-blue-500/40",
  dealer: "bg-purple-500/15 text-purple-300 border-purple-500/40",
  contract: "bg-lojack-clause-500/15 text-lojack-clause-300 border-lojack-clause-500/40",
  computed: "bg-gray-700/40 text-gray-300 border-gray-600",
};

/**
 * Scroll the EditCustomerModal to the input whose ``data-field-key`` matches
 * ``placeholderKey`` and flash a 2-second amber ring to highlight it. Returns
 * ``true`` if the field was found, ``false`` otherwise (caller can show a
 * toast for keys that don't map to any form field — e.g. ``contract.*``
 * fields live elsewhere).
 */
const scrollToFormField = (placeholderKey) => {
  const wrap = document.querySelector(`[data-field-key="${placeholderKey}"]`);
  if (!wrap) return { found: false, inputState: "not-found" };
  wrap.scrollIntoView({ behavior: "smooth", block: "center" });
  // Flash highlight — works whether wrap is an input or a wrapping div
  wrap.classList.add("ring-2", "ring-amber-400", "ring-offset-2", "ring-offset-[#1A1A1A]");
  setTimeout(() => {
    wrap.classList.remove("ring-2", "ring-amber-400", "ring-offset-2", "ring-offset-[#1A1A1A]");
  }, 2200);
  // Focus the inner input if present so the admin can start typing immediately.
  // Returns a status hint so the caller can show a toast when the field is
  // visible but locked (admin must enable override first).
  const input = wrap.matches("input,select,textarea") ? wrap : wrap.querySelector("input,select,textarea");
  let inputState = "no-input";
  if (input) {
    if (input.disabled) {
      inputState = "disabled";
    } else {
      try { input.focus(); inputState = "focused"; } catch { inputState = "error"; }
    }
  }
  return { found: true, inputState };
};

export const ContractTemplateDiagnosticsWidget = ({ customer, onMissingFieldsChange }) => {
  const { user, getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  // Visible only to admin / super_admin / it_admin roles (avoid dealer noise)
  const ROLES_ALLOWED = ["super_admin", "admin", "it_admin"];
  const visible = user && ROLES_ALLOWED.includes(user.role);

  useEffect(() => {
    if (!visible || !customer?.id) { setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/customers/${customer.id}/contract-template-coverage`,
          getAuthHeader(),
        );
        if (alive) {
          setData(r.data);
          // Lift the missing-mandatory list to the parent modal so it can
          // flag the matching form labels with a red asterisk (Fix E).
          if (onMissingFieldsChange) {
            onMissingFieldsChange(r.data.missing_mandatory || []);
          }
        }
      } catch (e) {
        console.warn("Template coverage load failed", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [visible, customer?.id]);

  if (!visible) return null;

  const renderRing = () => {
    const pct = data?.coverage_pct ?? 0;
    const color = pct >= 90 ? "#2B648F" : pct >= 60 ? "#6E0C0C" : "#ef4444";
    const circumference = 2 * Math.PI * 28;
    const dashOffset = circumference * (1 - pct / 100);
    return (
      <div className="relative w-20 h-20 shrink-0" data-testid="contract-coverage-ring">
        <svg width="80" height="80" viewBox="0 0 80 80">
          <circle cx="40" cy="40" r="28" stroke="#1f1f1f" strokeWidth="6" fill="none" />
          <circle
            cx="40" cy="40" r="28"
            stroke={color} strokeWidth="6" fill="none"
            strokeDasharray={circumference} strokeDashoffset={dashOffset}
            strokeLinecap="round"
            transform="rotate(-90 40 40)"
            style={{ transition: "stroke-dashoffset 600ms ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-white font-bold text-base tabular-nums">{pct}%</span>
        </div>
      </div>
    );
  };

  return (
    <div
      className="mt-6 bg-[#0d0d0d] border border-gray-800 rounded-2xl p-5"
      data-testid="contract-template-diagnostics-widget"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl bg-lojack-clause-500/15 text-lojack-clause-400 flex items-center justify-center shrink-0">
          <FileText size={17} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-sm">Diagnostica template Zorzutti v2</h3>
          <p className="text-gray-500 text-xs mt-0.5">
            Verifica per-cliente che la piattaforma raccolga tutti i dati del contratto LoJack v2 prima del go-live
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-gray-500 text-xs">
          <Loader2 size={14} className="animate-spin mr-2" /> Caricamento diagnostica…
        </div>
      ) : !data ? (
        <div className="text-gray-500 text-xs py-4 text-center">Diagnostica non disponibile.</div>
      ) : (
        <>
          {/* Top: Coverage ring + summary */}
          <div className="flex items-center gap-4 mb-3">
            {renderRing()}
            <div className="flex-1 min-w-0">
              <p className="text-gray-300 text-sm">
                <strong className="text-white">{data.resolved}</strong>
                <span className="text-gray-500"> / {data.total_placeholders}</span>
                <span className="text-gray-500"> placeholder risolti</span>
              </p>
              {data.missing_mandatory.length === 0 ? (
                <p className="text-lojack-blue-300 text-xs mt-1 flex items-center gap-1.5" data-testid="contract-coverage-complete">
                  <CheckCircle2 size={12} /> Tutti i campi obbligatori sono popolati — cliente pronto per la firma
                </p>
              ) : (
                <p className="text-lojack-clause-300 text-xs mt-1 flex items-center gap-1.5" data-testid="contract-coverage-incomplete">
                  <AlertTriangle size={12} />
                  <strong>{data.missing_mandatory.length}</strong> campi obbligatori mancanti
                </p>
              )}
            </div>
          </div>

          {/* Missing mandatory list */}
          {data.missing_mandatory.length > 0 && (
            <div className="bg-lojack-clause-500/5 border border-lojack-clause-500/20 rounded-xl p-3 mb-3" data-testid="contract-coverage-missing-list">
              <p className="text-lojack-clause-300 text-[10px] uppercase tracking-wider font-semibold mb-2">
                Campi obbligatori mancanti — click per saltare al campo
              </p>
              <div className="flex flex-wrap gap-1.5">
                {data.missing_mandatory.map((key) => {
                  const placeholder = data.placeholders.find((p) => p.key === key);
                  const label = placeholder?.label_it || key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        const res = scrollToFormField(key);
                        if (!res.found) {
                          toast.warning(
                            `"${label}" non è modificabile dal form — il dato vive in un'altra sezione (contratto / sistema).`
                          );
                        } else if (res.inputState === "disabled") {
                          // Field exists but is locked — common case when
                          // admin clicks a pill without first enabling override.
                          toast.info(
                            `"${label}" è bloccato dal contratto firmato. Attiva "Override admin" per modificarlo.`
                          );
                        }
                      }}
                      className="text-[11px] bg-lojack-clause-500/15 hover:bg-lojack-clause-500/30 text-lojack-clause-200 border border-lojack-clause-500/30 hover:border-lojack-clause-400 rounded-full px-2 py-0.5 transition-colors cursor-pointer"
                      title={`Click per saltare al campo "${label}" (${key})`}
                      data-testid={`contract-missing-${key}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Expandable full list */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between text-xs text-gray-400 hover:text-white border-t border-gray-800 pt-3 transition-colors"
            data-testid="contract-coverage-toggle"
          >
            <span>{expanded ? "Nascondi" : "Mostra"} mappatura completa dei 35 placeholder</span>
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {expanded && (
            <div className="mt-3 max-h-72 overflow-y-auto" data-testid="contract-coverage-full-list">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#0d0d0d]">
                  <tr className="text-left text-gray-500 text-[10px] uppercase tracking-wider">
                    <th className="pb-2 pr-2">Origine</th>
                    <th className="pb-2 pr-2">Campo</th>
                    <th className="pb-2">Valore</th>
                  </tr>
                </thead>
                <tbody>
                  {data.placeholders.map((p) => {
                    const isMissing = !p.value || p.value === "—";
                    return (
                      <tr key={p.key} className="border-t border-gray-800">
                        <td className="py-1.5 pr-2 align-top">
                          <span className={`inline-block text-[9px] px-1.5 py-0.5 rounded border ${SOURCE_BADGE[p.source] || SOURCE_BADGE.computed}`}>
                            {p.source}
                          </span>
                        </td>
                        <td className="py-1.5 pr-2 align-top">
                          <span className="text-gray-300">{p.label_it}</span>
                          {p.optional && <span className="text-gray-600 text-[10px] ml-1">(opz.)</span>}
                        </td>
                        <td className="py-1.5 align-top font-mono text-[11px]">
                          {isMissing ? (
                            <span className={p.optional ? "text-gray-600" : "text-lojack-clause-400"}>
                              {p.optional ? "—" : "MANCANTE"}
                            </span>
                          ) : (
                            <span className="text-white break-all">{p.value}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ContractTemplateDiagnosticsWidget;

ContractTemplateDiagnosticsWidget.propTypes = {
  customer: PropTypes.object.isRequired,      // customer record for which the contract template is diagnosed
  onMissingFieldsChange: PropTypes.func,      // optional: lifts missing_mandatory array to the parent
};
